import type { TaxDashboardSummary } from '@shared/api';
import { IconArrowRight } from '@shared/ui/icons';
import { formatMillions } from '../financierFormat';
import {
  coverageText,
  moscowTodayIso,
  quarterLabel,
  quarterOf,
  vatDifference,
} from '../workingCapital/taxPeriods';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionHeaderSub,
  SectionLink,
  TaxSummaryGrid,
  MetricBox,
  MetricLabel,
  MetricValue,
  MetricNote,
} from './styled';

interface TaxPositionSectionProps {
  tax: TaxDashboardSummary;
}

export function TaxPositionSection({ tax }: TaxPositionSectionProps) {
  // Пульт получает оценку за текущий квартал по Москве — так его и подписываем
  const quarter = quarterLabel(quarterOf(moscowTodayIso()));
  const difference = vatDifference(tax.vat_outgoing_signed, tax.vat_incoming_deductible);

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Оценка НДС за {quarter} и налоговый резерв</SectionTitle>
        <SectionHeaderSub>
          По всем компаниям. Это оценка, а не данные учёта: сумма к уплате — по декларации
        </SectionHeaderSub>
        <SectionLink to="/agents/finance/working-capital">
          Календарь ЕНС и авансы
          <IconArrowRight size={14} />
        </SectionLink>
      </SectionHeader>
      <TaxSummaryGrid>
        <MetricBox>
          <MetricLabel>Оценка исходящего НДС по подписанным КС-3 за {quarter}</MetricLabel>
          <MetricValue>{formatMillions(tax.vat_outgoing_signed)}</MetricValue>
          <MetricNote>Ставка НДС — на дату подписания КС-3</MetricNote>
        </MetricBox>
        <MetricBox>
          <MetricLabel>Оценка входящего НДС по одобренным заявкам</MetricLabel>
          <MetricValue>{formatMillions(tax.vat_incoming_deductible)}</MetricValue>
          <MetricNote>Одобренные, выгруженные в 1С и исполненные заявки за {quarter}</MetricNote>
        </MetricBox>
        <MetricBox>
          <MetricLabel>Разница (оценка)</MetricLabel>
          <MetricValue $color={difference > 0 ? '#ef4444' : '#10b981'}>
            {formatMillions(difference)}
          </MetricValue>
          <MetricNote>
            {difference < 0 ? 'Входящий НДС больше исходящего' : 'Исходящий минус входящий'}
          </MetricNote>
        </MetricBox>
        <MetricBox>
          <MetricLabel>Остаток на счёте налогового резерва</MetricLabel>
          <MetricValue $color="#8b5cf6">{formatMillions(tax.tax_reserve_balance)}</MetricValue>
          <MetricNote>
            Покрытие разницы: <b>{coverageText(tax.reserve_coverage_pct)}</b>
          </MetricNote>
        </MetricBox>
      </TaxSummaryGrid>
    </Section>
  );
}
